import { Alert, Button, Checkbox, Form, Input, InputNumber, Modal, Pagination, Segmented, Select, Table, TreeSelect, Upload } from 'antd'
import { useEffect, useRef, useState, useTransition } from 'react'
import { useHistory } from 'react-router'
import publisherApi from 'src/component/app/publisher/PublisherApi'
import { AccountBean, DspBean, LanguageBean, PublisherBean, TimeZoneBean, CountryBean } from '../../../api/api'
import { formDataLoader, FormDataType } from '../../../util/FormDataLoader'
import { RevenueCalculationMethod, SellerType, getDefaultIabCategoryIds, getIabCategoriesAsTree, resolveIabDefaultsChange, stripSelectDefaultsValue, truncateErrorMessage } from '../../../util/Util'
import { InputCSV } from './inputCSV/InputCSV'
import { CheckCircleOutlined, CloseOutlined, EyeOutlined, PlusOutlined, StopOutlined, UploadOutlined } from '@ant-design/icons';
import { StorageKey, storageWriterFactory } from '../../../util/Storage'
import PublisherDomainListCard from './PublisherDomainListCard'
const FormItem = Form.Item
const Option = Select.Option

const formInfoItems: FormDataType[] = [
    FormDataType.DspList,
    FormDataType.IabCategories,
    FormDataType.Languages,
    FormDataType.TimeZones,
    FormDataType.Publishers,
    FormDataType.Countries
]

const WinNotificationEnum = DspBean.WinNotificationEnum

const PublisherCreate = () => {
    const [error, setError] = useState(undefined)
    const [validation, setValidation] = useState(undefined);
    const [useParentSellerAccountId, setuseParentSellerAccountId] = useState(false);
    const [parentPublisherId, setParentPublisherId] = useState(undefined);
    const [passThrough, setpassThrough] = useState(false);
    const [includeDomains, setIncludeDomains] = useState(false);
    const [form] = Form.useForm()
    const [saveMode, setSaveMode] = useState<'SAVE' | 'DRAFT'>('SAVE');
    const storageWriter = storageWriterFactory('publisher');
    const history = useHistory()

    const [allClassification, setAllClassification] = useState(false)
    const [acceptAllMobileAppIds, setAcceptAllMobileAppIds] = useState(false)
    const [domainMobileError, setDomainMobileError] = useState('')

    // ─── Domains state ──────────────────────────────────────────────────────
    const [domains, setDomains] = useState<string[]>([])
    const [domainsCollapsed, setDomainsCollapsed] = useState(false)

    // ─── Mobile App IDs state ───────────────────────────────────────────────
    const [mobileAppIds, setMobileAppIds] = useState<string[]>([])
    const [mobileAppsCollapsed, setMobileAppsCollapsed] = useState(false)

    const iabPrevRef = useRef<number[]>([])

    const [info, setInfo] = useState({
        dspList: [],
        iabCategories: [],
        languages: [],
        timezones: [],
        publishers: [],
        countries: [],
    })

    const handleCountrySelect = (value: number | string) => {
        const allIds = (info.countries ?? [])
            .map((c: CountryBean) => c.id)
            .filter((v): v is number => v !== undefined && v !== null)

        if (value === 'All') {
            form.setFieldsValue({ countries: ['All', ...allIds] })
        } else {
            const current: (number | string)[] = form.getFieldValue('countries') ?? []
            const selected = Array.from(new Set([...current.filter((v) => v !== 'All'), value]))
            if (selected.length === allIds.length) {
                form.setFieldsValue({ countries: ['All', ...selected] })
            }
        }
    }

    const handleCountryDeselect = (value: number | string) => {
        if (value === 'All') {
            form.setFieldsValue({ countries: [] })
        } else {
            const current: (number | string)[] = form.getFieldValue('countries') ?? []
            form.setFieldsValue({ countries: current.filter((v) => v !== 'All' && v !== value) })
        }
    }

    const navigateToLastPage = async () => {
        try {
          const response = await publisherApi.list(1, 1);
          const total = response.pagination?.total || 1;
          const pageSize = 10;
          const lastPage = Math.ceil(total / pageSize);
      
          storageWriter.write(StorageKey.SearchParams, {
            page: lastPage,
          });
      
          history.push('/publishers', { fromPublisherList: true });
        } catch {
          history.push('/publishers', { fromPublisherList: true });
        }
      };
    const validateDomainsOrMobileApps = (): boolean => {
        if (allClassification || acceptAllMobileAppIds) {
            setDomainMobileError('')
            return true
        }
        if (domains.length === 0 && mobileAppIds.length === 0) {
            setDomainMobileError('Either Domains or Mobile App IDs is required')
            return false
        }
        setDomainMobileError('')
        return true
    }

    const handleSubmit = () => {

        // Draft → validate only publisher name
        if (saveMode === 'DRAFT') {
          form
            .validateFields(['name'])
            .then(() => {
              const allValues = form.getFieldsValue(true);
              createPublisher(allValues, true);
            })
            .catch(() => {});
          return;
        }

        // Save → full validation
        form
          .validateFields()
          .then(() => {
            if (!validateDomainsOrMobileApps()) return;
            const allValues = form.getFieldsValue(true);
            createPublisher(allValues, false);
          })
          .catch(() => {});
    };

    const createPublisher = (values: any, isDraft: boolean) => {
        const domainList: string[] = allClassification ? [] : domains
        const mobileAppIdList: string[] = acceptAllMobileAppIds ? [] : mobileAppIds
        const countries = values?.countries
            ?.filter((countryId: number | string) => countryId !== 'All')
            .map((countryId: number) => ({ id: countryId })) || [];
        delete values.blackListDomainsField;

        publisherApi
          .create({
            ...values,
            iabCategoriesIds: stripSelectDefaultsValue(values.iabCategoriesIds),
            domains: domainList,
            acceptAllDomains: allClassification,
            mobileAppIds: mobileAppIdList,
            acceptAllMobileAppIds,
            countries,
            validation: validation || false,
            useParentSellerAccountId,
            passThrough,
            ...(isDraft && { status: 'DRAFT' }),
          })
          .then((res) => {
            if (isDraft) {
              Modal.info({
                title: 'Publisher saved as Draft',
                content: 'This publisher is in Draft status. Activate it to enable bidding and generate API credentials.',
                okText: 'OK',
                onOk: () => navigateToLastPage(),
              });
            } else {
              activatePublisher(res.data.id);
            }
          })
          .catch(onRejected);
      };
      const activatePublisher = (publisherId: number) => {
        publisherApi
          .activate(publisherId)
          .then(response => {
            if (response?.data?.authDataBean) {
              userCredentials(response.data.authDataBean);
            } else {
                navigateToLastPage();
            }
          })
          .catch(err => {
            Modal.error({
              title: 'Activation Failed',
              content: err?.error || 'Unable to activate publisher',
            });
          });
      };
      const userCredentials = (authDataBean) => {
        Modal.info({
            title: 'New account created',
            content: <div>
                <br />
                <div>Please save account credentials</div>
                <br />
                <div>Username: {authDataBean.username}</div>
                <div>Password: {authDataBean.password}</div>
            </div>,
            okText: 'OK',
            okCancel: false,
            onOk: () => navigateToLastPage(),
            
        });
    };            

    

    const onRejected = err => {
        setError(truncateErrorMessage(err?.error))
    }

    const loadData = async () => {
        try {
            let formInfo = await formDataLoader(...formInfoItems)
            setInfo(formInfo as any)
            let iabs = getDefaultIabCategoryIds(formInfo.iabCategories as any);
            let dsps = formInfo?.dspList?.map((el: DspBean) => el?.id)
            form.setFieldsValue({ 
                ['iabCategoriesIds']: iabs,
                ['dspBlackListIds']: dsps,
            })
        } catch (e) {
            onRejected(e)
        }
    }

    useEffect(() => {
        loadData()
    }, [])

    const handleSelectAll = (value: any) => {
        if (value && value.length && value.includes('__all')) {
            if (value.length === info.dspList.length + 1) {
                return []
            }
            return [...(info.dspList.map((el: DspBean) => el.id))]
        }
        return value
    }

    const onChangeIab = (value: number[]) => {
        const next = resolveIabDefaultsChange(value || [], iabPrevRef.current, info.iabCategories as any)
        iabPrevRef.current = next
        form.setFieldsValue({ ['iabCategoriesIds']: next })
        return next
    }

    const handleValidation = (value) => {
        setValidation(value.target.checked)
    };
    const handleuseParentSellerAccountId = (value) => {
        setuseParentSellerAccountId(value.target.checked)
    };
    const handleParentPublisherId =(event)=>{
        setParentPublisherId(event)
    }
    const handlepassThrough = (value) => {
        setpassThrough(value.target.checked)
    };
    const handleIncludeDomains = (value) => {
        setIncludeDomains(value.target.checked)
    };

    useEffect(() => {
        if (!useParentSellerAccountId) {
          form.setFields([{ name: "parentPublisherId", errors: [] }]);
        }
    }, [useParentSellerAccountId]);

    // ─── Domain handlers ────────────────────────────────────────────────────
    const handleAddDomain = (value: string) => setDomains(prev => [...prev, value])
    const handleUploadDomains = (file: File) => {
        const reader = new FileReader()
        reader.onloadend = () => {
            const lines = (reader.result as string).split('\n').map(l => l.trim()).filter(Boolean)
            setDomains(prev => {
                const existingLower = new Set(prev.map(d => d.toLowerCase()))
                return [...prev, ...lines.filter(d => !existingLower.has(d.toLowerCase()))]
            })
        }
        reader.readAsText(file)
    }
    const handleRemoveDomain = (index: number) =>
        setDomains(prev => prev.filter((_, i) => i !== index))
    const handleClearAllDomains = () => setDomains([])

    // ─── Mobile App ID handlers ─────────────────────────────────────────────
    const handleAddMobileAppId = (value: string) => setMobileAppIds(prev => [...prev, value])
    const handleUploadMobileAppIds = (file: File) => {
        const reader = new FileReader()
        reader.onloadend = () => {
            const lines = (reader.result as string).split('\n').map(l => l.trim()).filter(Boolean)
            setMobileAppIds(prev => {
                const existingLower = new Set(prev.map(d => d.toLowerCase()))
                return [...prev, ...lines.filter(d => !existingLower.has(d.toLowerCase()))]
            })
        }
        reader.readAsText(file)
    }
    const handleRemoveMobileAppId = (index: number) =>
        setMobileAppIds(prev => prev.filter((_, i) => i !== index))
    const handleClearAllMobileAppIds = () => setMobileAppIds([])

    return (
        <div>
            {error && <div><Alert message={error} type="error" /><br /></div>}
            <Form
                style={{
                    maxWidth: '1000px'
                }}
                onFinish={handleSubmit}
                form={form}
                labelCol={{ span: 7 }}
                wrapperCol={{ span: 17 }}
            >
                <FormItem
                    name="name"
                    rules={[
                        {
                            required: true,
                            message: 'Please input Publisher name'
                        },
                        {
                            max: 255,
                            message: 'Name cannot be longer that 255 characters'
                        }
                    ]}
                    label="Name"
                    tooltip={'Name of the publisher'}
                    validateTrigger={false}
                >
                    <Input />
                </FormItem>
                <FormItem
                    name="sellerAccountId"
                    rules={[
                        {
                            type: 'integer',
                            min: 1,
                            max: 9999,
                            message: 'Please input a valid id from 1 to 9999',
                        }
                    ]}
                    label="SellerAccountId"
                    tooltip={'SellerAccountId of the publisher'}
                >
                    <InputNumber style={{ width: '100%' }} controls={false} disabled />
                </FormItem>
                <FormItem
                    name="language"
                    label="Language"
                    tooltip={'Publisher language'}
                    rules={[
                        {
                            max: 255,
                            message: 'Language cannot be longer that 255 characters'
                        }
                    ]}
                    validateTrigger={false}
                >
                    <Select
                        optionFilterProp="title"
                        showSearch
                        allowClear
                        placeholder="Select language"
                    >
                        {info.languages.map((item: LanguageBean) => (
                            <Option title={item.name} key={item.id} value={item.id as string}>{item.name}</Option>))}
                    </Select>
                </FormItem>
                <FormItem
                    name="iabCategoriesIds"
                    label="Publisher IAB Categories"
                    tooltip={'IAB categories publisher wants to work with'}
                >
                    <TreeSelect
                        allowClear
                        treeCheckable
                        showCheckedStrategy="SHOW_ALL"
                        style={{ maxHeight: '400px', overflow: 'auto' }}
                        placement={'topRight'}
                        placeholder="Select IAB categories"
                        onChange={onChangeIab}
                        treeData={getIabCategoriesAsTree(info.iabCategories)}
                    />
                </FormItem>
                <FormItem
                    name="dspBlackListIds"
                    label="Dsp blacklist"
                    getValueFromEvent={handleSelectAll}
                    tooltip={'Dsps to do not get ads from'}
                >
                    <Select
                        showSearch
                        allowClear
                        mode="multiple"
                        placeholder="Select dsp"
                    >
                        {info.dspList.length > 0 && <Option value={'__all'}>{'Select all dsps'}</Option>}
                        {info.dspList.map((item: DspBean) => (
                            <Option key={item.id} value={item.id as number}>{item.name}</Option>
                        ))}
                    </Select>
                </FormItem>
                {/* <FormItem
                    name="blackListDomainsField"
                    label="Filter Mode"
                    initialValue="white"
                    tooltip={'Choose Domain filter mode'}
                >
                    <Segmented
                        options={[
                            // {
                            //     label: 'Block list',
                            //     value: 'black',
                            //     icon: <StopOutlined />,
                            // },
                            {
                                label: 'Allow list',
                                value: 'white',
                                icon: <CheckCircleOutlined />
                            },
                        ]}
                    />
                </FormItem> */}
                <FormItem
                    label="Allowed Domains"
                    tooltip={'Publisher allowed domains with classifications'}
                    validateStatus={domainMobileError ? 'error' : undefined}
                    help={domainMobileError || undefined}
                >
                    <PublisherDomainListCard
                        title="Web Domains"
                        subtitle="Manage allowlist entries"
                        items={domains}
                        acceptAll={allClassification}
                        collapsed={domainsCollapsed}
                        onToggleCollapse={() => setDomainsCollapsed(v => !v)}
                        onAcceptAllChange={checked => {
                            setAllClassification(checked)
                            if (checked) setDomains([])
                            setDomainMobileError('')
                        }}
                        onAdd={handleAddDomain}
                        onUpload={handleUploadDomains}
                        onRemoveItem={handleRemoveDomain}
                        onClearAll={handleClearAllDomains}
                    />
                </FormItem>

                <FormItem
                    name="includeDomains"
                    label="Include Allowed Domains in sellers.JSON"
                    valuePropName="checked"
                >
                    <Checkbox
                        checked={includeDomains}
                        onChange={handleIncludeDomains}
                    >
                    </Checkbox>
                </FormItem>

                <FormItem
                    label="Allowed Mobile App IDs"
                    tooltip={'Publisher allowed mobile app IDs with classifications'}
                    validateStatus={domainMobileError ? 'error' : undefined}
                    help={domainMobileError || undefined}
                >
                    <PublisherDomainListCard
                        title="MobileApp ID"
                        subtitle="Manage allowlist entries"
                        items={mobileAppIds}
                        acceptAll={acceptAllMobileAppIds}
                        collapsed={mobileAppsCollapsed}
                        onToggleCollapse={() => setMobileAppsCollapsed(v => !v)}
                        onAcceptAllChange={checked => {
                            setAcceptAllMobileAppIds(checked)
                            if (checked) setMobileAppIds([])
                            setDomainMobileError('')
                        }}
                        onAdd={handleAddMobileAppId}
                        onUpload={handleUploadMobileAppIds}
                        onRemoveItem={handleRemoveMobileAppId}
                        onClearAll={handleClearAllMobileAppIds}
                    />
                </FormItem>

                <FormItem
                    name="winNotification"
                    label="Win notification"
                    initialValue="Nurl"
                >
                    <Select
                        allowClear
                        placeholder="Select win notification type">
                        {Object.values(WinNotificationEnum).map(win =>
                            <Option key={win} value={win}>{win}</Option>)}
                    </Select>
                </FormItem>

                <FormItem
                    name="bundle"
                    rules={[
                        {
                            max: 255,
                            message: 'Bundle cannot be longer that 255 characters'
                        }
                    ]}
                    label="Bundle"
                    tooltip={'Publisher\'s App bundle'}
                >
                    <Input />
                </FormItem>
                <FormItem
                    name="storeUrl"
                    rules={[
                        {
                            max: 255,
                            message: 'Store URL cannot be longer that 255 characters'
                        }
                    ]}
                    label="Store URL"
                    tooltip={'Publisher\'s App store URL'}
                >
                    <Input />
                </FormItem>
                {/* <FormItem
                    name="publisherDomain"
                    rules={[
                        {
                            max: 255,
                            message: 'Store URL cannot be longer that 255 characters'
                        }
                    ]}
                    label="Publisher domain"
                    tooltip={'Publisher domain'}
                >
                    <Input />
                </FormItem> */}
                <FormItem
                    name="auctionMode"
                    label="Auction Mode"
                    rules={[{ required: true, message: 'Please select Auction type' }]}
                    tooltip={'Auction type, where 1 = First Price, 2 = Second Price Plus. Exchange-specific auction types can be defined using values greater than 500'}
                >
                    <Select placeholder="Select Auction Mode" >
                        {Object
                            .keys(PublisherBean.AuctionModeEnum)
                            .map((mode) => <Option key={mode} value={mode}>{mode.replace(/([a-z])([A-Z])/g, '$1 $2')}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="houseBidderMode"
                    label="House Bidder Mode"
                    rules={[{ required: true, message: 'Please select House Bidder Mode' }]}
                    tooltip={'House Bidder Mode'}
                >
                    <Select placeholder="Select House Bidder Mode" >
                        {Object
                            .keys(PublisherBean.HouseBidderModeEnum)
                            .map((mode) => <Option key={mode} value={mode}>{mode.replace(/([a-z])([A-Z])/g, '$1 $2')}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="maxQps"
                    label="QPS"
                    tooltip={'Maximum queries per second allowed'}
                    rules={[
                        {   
                            pattern: /^\d*$/,
                            message: 'Please input a valid number',
                        }
                    ]}
                >
                    <Input />
                </FormItem>
                <FormItem
                    required
                    label="Alkimi Exchange Fee"
                    tooltip="Publisher Fee"
                >
                    <FormItem
                        name="fee"
                        initialValue={3}
                        rules={[{ required: true, message: 'Fee is required' }]}
                        className="ant-item ant-item-first"
                    >
                        <InputNumber step={0.01} min={0} max={99} />
                    </FormItem>
                    <FormItem className="ant-item ant-item-second">%</FormItem>
                </FormItem>
                <FormItem
                    name="sellerDomain"
                    label="Seller Domain"
                    rules={[
                        {
                            required: true,
                            message: 'Please input Seller Domain'
                        }
                    ]}
                    tooltip={'Seller domains (domain1)'}
                >
                    <Input />

                </FormItem>
                <FormItem
                    name="sellerType"
                    label="Seller Type"
                    rules={[{ required: true, message: 'Please select Seller type' }]}
                    // tooltip={'Seller type, where 1 = First Price, 2 = Second Price Plus. Exchange-specific auction types can be defined using values greater than 500'}
                >
                    <Select placeholder="Select Seller type" >
                            {SellerType.map(element=><Option key={element.value} value={element.value}>{element.label}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="revenueCalculationMethod"
                    label="Revenue Calculation Method"
                    rules={[{ required: true, message: 'Please select Revenue Calculation Method' }]}
                    // tooltip={'Seller type, where 1 = First Price, 2 = Second Price Plus. Exchange-specific auction types can be defined using values greater than 500'}
                >
                    <Select placeholder="Select Revenue Calculation Method" >
                            {RevenueCalculationMethod.map(element=><Option key={element.value} value={element.value}>{element.label}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="passThrough"
                    label="Pass Through"
                    valuePropName="checked"
                    // tooltip={'If true publisher should send signed request only'}
                >
                    <Checkbox
                        onChange={handlepassThrough}
                    />
                </FormItem>
               
                <FormItem
                    name="useParentSellerAccountId"
                    label="Use Parent Seller Id"
                    valuePropName="checked"
                    // tooltip={'If true publisher should send signed request only'}
                >
                    <Checkbox
                        onChange={handleuseParentSellerAccountId}
                        // disabled={!parentPublisherId}
                    />
                </FormItem>
                <FormItem
                    name="parentPublisherId"
                    label="Parent Publisher"
                    rules={useParentSellerAccountId
                        ? [
                            {
                              required: true,
                              message: 'Please select parent publisher id'
                            }
                          ]
                        : []
                      }
                      

                    // getValueFromEvent={handleSelectAll}
                    tooltip={'Select Parent Publisher Id'}
                >
                    <Select
                        showSearch
                        allowClear
                        // mode="multiple"
                        onChange={handleParentPublisherId}
                        placeholder="Select parent publisher id"
                        filterOption={(input, option) => 
                            option?.children?.toLowerCase()?.includes(input?.toLowerCase())
                        }
                    >
                        {/* {info.dspList.length > 0 && <Option value={'__all'}>{'Select all dsps'}</Option>} */}
                        {info?.publishers?.map((item: DspBean) => (
                            <Option key={item.id} value={item.id as number}>{item.name}</Option>
                        ))}
                    </Select>
                </FormItem>
               
                <FormItem
                    name="validation"
                    label="Validation"
                    valuePropName="checked"
                    tooltip={'If true publisher should send signed request only'}
                >
                    <Checkbox
                        onChange={handleValidation}
                    />
                </FormItem>
                <FormItem
                    name="publicValidationKey"
                    label="Public validation key"
                    rules={[
                        {
                            max: 255,
                            message: 'Public validation key cannot be longer that 255 characters'
                        }
                    ]}
                    tooltip={'Public validation key need to validate publisher request'}
                >
                    <Input disabled={!validation} />
                </FormItem>
                {/* <FormItem
                    name="active"
                    label="Active"
                    valuePropName="checked"
                    initialValue={true}
                    tooltip={'If true publisher can send requests to exchange'}
                >
                    <Checkbox />
                </FormItem> */}
                <FormItem
                    name="email"
                    rules={[
                        { required: true, message: 'Please input Publisher email' },
                        {
                            pattern: new RegExp('.+@.+\\..+'),
                            message: 'Email is incorrect'
                        }
                    ]}
                    label="Email"
                >
                    <Input />
                </FormItem>
                <FormItem
                    name="timezoneId"
                    rules={[{ required: true, message: 'Please select Time Zone', type: 'string' }]}
                    label="Time Zone"
                >
                    <Select
                        showSearch
                        placeholder="Select time zone">
                        {info.timezones.map((timezone: TimeZoneBean) =>
                            <Option key={timezone.id} value={timezone.id as string}>{timezone.id}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="test"
                    label="Restrict Blockchain Transaction"
                    valuePropName="checked"
                    tooltip="If true publisher transactions will not be sent to Blockchain ledger"
                >
                    <Checkbox />
                </FormItem>
                <FormItem
                    required
                    label="Validations limit"
                    tooltip="Limit the number of validation report per month"
                >
                    <FormItem
                        name="athenaValidationsLimit"
                        rules={[{ required: true, message: 'Limit is required' }]}
                        className="ant-item ant-item-first"
                    >
                        <InputNumber min={0} defaultValue={0} />
                    </FormItem>
                </FormItem>
                <FormItem
                    required
                    label="Request/Response Id period limit"
                    tooltip="Limit of period for Request/Response Id report in hours per month"
                >
                    <FormItem
                        name="athenaPeriodLimit"
                        rules={[{ required: true, message: 'Limit is required' }]}
                        className="ant-item ant-item-first"
                    >
                        <InputNumber min={0} defaultValue={0} />
                    </FormItem>
                </FormItem>
                <FormItem
                name="countries"
                label="Geo Countries"
                tooltip="Geo Countries"
            >
                 <Select
                        mode="multiple"
                        showSearch
                        allowClear
                        placeholder="Select Countries"
                        optionFilterProp="children"
                        onSelect={handleCountrySelect}
                        onDeselect={handleCountryDeselect}
                        filterOption={(input, option) =>
                            (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        <Option key="All" value="All">All</Option>
                        {info.countries.map((item: CountryBean) => (
                            <Option key={item.id} value={item.id}>{item.name}</Option>
                        ))}
                    </Select>
            </FormItem>
            </Form>
            <Form
                style={{
                    maxWidth: '1000px'
                }}
                onFinish={handleSubmit}>
                <FormItem
                    wrapperCol={{
                        offset: 6, span: 18
                    }}>
                        <Button
                            style={{ width: '30%', margin: '0 1% 1% 0' ,background: '#10b981b8',color:'#fff'}}
                            type="default"
                            htmlType="submit"
                            onClick={() => setSaveMode('DRAFT')}
                            >
                            Save as Draft
                        </Button>
                    <Button style={{
                        width: '30%',
                        margin: '0 0 1% 0'
                    }} type="primary" htmlType="submit" onClick={() => setSaveMode('SAVE')}>Activate</Button>
                    <Button style={{
                        width: '30%',
                        margin: '0 0 0 1%'
                    }} type="primary" danger htmlType="button" onClick={() => history.push('/publishers', { fromPublisherList: true })}>Cancel</Button>
                </FormItem>
            </Form>
        </div>
    )
}

export default PublisherCreate